import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import type {DateRange} from "../../../common/newDatePicker/datePickerShared";
import {
    InsightsSeriesChart,
    InsightsSeriesLegend,
    SeriesColors,
    SeriesSpec,
} from "../../../surveys/insights/charts/InsightsSeriesChart";
import {CardHeadline} from "../../kit/Headline";
import {CHART_HEIGHT} from "../../kit/insightsChart";
import {calendarDayToTimestamp} from "../../kit/insightsFormat";
import type {InsightsLoading} from "../../kit/insightsLoading";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {SeriesCard} from "../../kit/SeriesCard";
import {TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import type {
    CrmOverviewTimeSeriesPointV4,
    CrmOverviewTimeSeriesV4,
    InsightTileV4,
} from "../../models/crm-v4";
import {insightCurrency} from "../base/insightCurrency";
import {formatDeltaMagnitude, formatStatValue, unitLabel} from "../base/insightStatCardFormatting";
import {TARGET_METRICS_BY_ID} from "../targets/targetDefinitions";
import {CALENDAR_MONTH_KEYS, currentCalendarMonthKey} from "../base/InsightCurrentMonthHighlight";
import {TrendBucket, useBucketLabels} from "./crmTrendBuckets";
import {CRM_TREND_METRICS, CrmTrendMetricKey} from "./crmTrendMetrics";

interface CrmTrendRow {
    index: number;
    /** The bucket's start day, which is what makes a row unique. */
    key: string;
    timestamp: number | null;
    compareTimestamp: number | null;
    /** The bucket as it is read: "August 2025" monthly, "12 Aug 2025" finer. */
    full: string;
    compareFull: string | null;
    value: number | null;
    compare: number | null;
    /** This bucket's budget: flat for a rate, the running total so far for a cumulative metric. */
    target: number | null;
}

export interface CrmTrendCardProps {
    metric: CrmTrendMetricKey;
    data?: CrmOverviewTimeSeriesV4;
    /**
     * The range figure, as the stat card above already read it. The card never derives its headline from
     * the buckets: an average recomputed from a series is how one metric ends up stated twice.
     */
    tile?: InsightTileV4;
    /** The selected period. The plot is bucket-indexed, so the card reads its dates off the buckets. */
    range: DateRange;
    /** One value per bucket, from `resolveChartTargets` — constant for a rate, rising for a running total. */
    targets?: Array<number | null>;
    loading?: InsightsLoading;
}

const AXIS_TICKS = 5;
const AXIS_PADDING = 0.08;
const NICE_STEPS = [1, 2, 2.5, 5, 10];

/** A step the eye reads as round, whatever the metric's magnitude: euros and percent share one axis rule. */
function niceStep(rough: number): number {
    if (!(rough > 0)) return 1;

    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
    const normalised = rough / magnitude;

    return (NICE_STEPS.find(step => normalised <= step) ?? 10) * magnitude;
}

/**
 * An axis around the values actually plotted rather than one anchored at zero: an ADR moving between 180
 * and 220 is a flat line on a zero-based scale. The foot never goes below zero, as none of these can.
 */
function axisFor(values: Array<number | null>): {domain: [number, number]; ticks: number[]} {
    const present = values.filter((value): value is number => value != null && Number.isFinite(value));

    if (present.length === 0) {
        return {domain: [0, 1], ticks: [0, 1]};
    }

    const low = Math.min(...present);
    const high = Math.max(...present);
    // One repeated value has no span of its own, so the axis opens around the value itself.
    const span = high - low || Math.abs(high) || 1;
    const padding = span * AXIS_PADDING;
    const step = niceStep((span + padding * 2) / AXIS_TICKS);

    const floor = Math.max(0, Math.floor((low - padding) / step) * step);
    const ceiling = Math.ceil((high + padding) / step) * step;
    const count = Math.max(1, Math.round((ceiling - floor) / step));

    return {
        domain: [floor, ceiling],
        ticks: Array.from({length: count + 1}, (_, index) => floor + index * step),
    };
}

function toRows(
    points: CrmOverviewTimeSeriesPointV4[],
    metric: CrmTrendMetricKey,
    buckets: TrendBucket[],
    compareBuckets: TrendBucket[],
    targets: Array<number | null> | undefined,
): CrmTrendRow[] {
    return points.map((point, index) => {
        const bucket = point[metric];

        return {
            index,
            key: point.from,
            timestamp: calendarDayToTimestamp(point.from),
            compareTimestamp: point.compareFrom == null ? null : calendarDayToTimestamp(point.compareFrom),
            full: buckets[index]?.full ?? point.from,
            compareFull: point.compareFrom == null ? null : compareBuckets[index]?.full ?? point.compareFrom,
            value: bucket.value,
            compare: bucket.compareValue ?? null,
            target: targets?.[index] ?? null,
        };
    });
}

/**
 * One overview metric across the selected period, against its comparison range and its budget. Which
 * metric is the only thing that varies, so it is configuration (`crmTrendMetrics.ts`) rather than a
 * fourth copy of this file.
 */
export function CrmTrendCard({metric, data, tile, targets, loading}: CrmTrendCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const currency = insightCurrency(useOrganization()?.currencyCode);

    const definition = CRM_TREND_METRICS[metric];
    // `OVERVIEW_STAT_METRICS` keyed by id, with the target's kind and scoring on top.
    const statMetric = TARGET_METRICS_BY_ID.get(metric);

    const formats = React.useMemo(() => definition.formats({locale, currency}), [definition, locale, currency]);

    const points = React.useMemo(() => data?.points ?? [], [data?.points]);
    // The comparison bucket is a different stretch of calendar, so it is named on its own dates.
    const comparePoints = React.useMemo(
        () => points.map(point => ({from: point.compareFrom ?? point.from})),
        [points],
    );
    const buckets = useBucketLabels(points, data?.granularity);
    const compareBuckets = useBucketLabels(comparePoints, data?.granularity);

    const rows = React.useMemo(
        () => toRows(points, metric, buckets, compareBuckets, targets),
        [points, metric, buckets, compareBuckets, targets],
    );

    const hasActual = rows.some(row => row.value != null);
    const hasComparison = rows.some(row => row.compare != null);
    const hasTarget = rows.some(row => row.target != null);
    const hasData = hasActual || hasComparison || hasTarget;

    /*
     * What the period as a whole is judged against: a rate carries the same target in every bucket, and a
     * cumulative metric's last step is the budget for exactly the stretch the series covers.
     */
    const budget = React.useMemo(() => {
        const values = rows.map(row => row.target).filter((value): value is number => value != null);

        return values.length === 0 ? undefined : values[values.length - 1];
    }, [rows]);

    const axis = React.useMemo(
        () => axisFor(rows.flatMap(row => [row.value, row.compare, row.target])),
        [rows],
    );

    const unit = unitLabel(tile?.unit);

    /*
     * The stat card above states this same figure. It is read off the same tile through the same
     * formatter, never recomputed from the buckets, so the two cannot drift apart by a rounding step.
     */
    const formatTileValue = (value: number | null | undefined) => {
        if (tile == null) return "—";

        const formatted = formatStatValue(tile.format, value, statMetric?.fractionDigits, currency, locale);

        return value != null && unit ? `${formatted} ${t(unit)}` : formatted;
    };

    const comparison = tile?.comparison;
    const title = t(definition.title);

    const headline = (
        <CardHeadline
            value={formatTileValue(tile?.value)}
            comparison={comparison == null ? undefined : {
                // The API judges each metric's direction, so the chip does not re-derive it.
                tone: comparison.tone,
                trend: comparison.trend ?? "neutral",
                change: formatDeltaMagnitude(comparison.delta, comparison.kind),
                previous: formatTileValue(tile?.compareValue ?? null),
            }}
            panel={comparison == null ? undefined : (
                <React.Fragment>
                    <TooltipTitle title={title}/>
                    <TooltipRow label={t("Current")} value={formatTileValue(tile?.value)}/>
                    <TooltipRow label={t("Compare period")} value={formatTileValue(tile?.compareValue ?? null)}/>
                </React.Fragment>
            )}
        />
    );

    // The month the period is still inside is only part-counted, so the axis says where "today" falls
    // rather than letting a short final bar read as a drop. Only monthly buckets are ambiguous this way.
    const currentMonthKey = currentCalendarMonthKey();
    const markerIndex = data?.granularity !== "MONTH" ? -1 : rows.findIndex(row => {
        if (row.timestamp == null) return false;
        const date = new Date(row.timestamp);

        return date.getFullYear() === new Date().getFullYear()
            && CALENDAR_MONTH_KEYS[date.getMonth()] === currentMonthKey;
    });

    const spec: SeriesSpec<CrmTrendRow> = {
        rows,
        valueKey: "value",
        compareKey: hasComparison ? "compare" : undefined,
        label: t("Actual"),
        compareLabel: t("Comparison"),
        target: budget == null ? undefined : {
            value: budget,
            verdict: "whole",
            label: t("Budget"),
            lowerIsBetter: statMetric?.scoring === "LOWER_IS_BETTER",
            // Judged on the range figure the stat card above states, not on whichever bucket came last.
            actual: tile?.value ?? null,
        },
        // Drawn per bucket rather than as one rule: a running total's budget climbs with it.
        targetKey: budget == null ? undefined : "target",
    };

    const tooltip = (row: CrmTrendRow, colors: SeriesColors) => (
        <React.Fragment>
            {/* A monthly bucket is titled by its first day up there, which is not what it covers. */}
            {data?.granularity === "MONTH" && <TooltipRow label={t("Time range")} value={row.full}/>}

            {row.value != null && (
                <TooltipRow swatch={colors.series} label={t("Actual")} value={formats.value(row.value)}/>
            )}

            {row.compare != null && (
                <TooltipRow
                    swatch={colors.compare}
                    label={row.compareFull == null ? t("Comparison") : `${t("Comparison")} · ${row.compareFull}`}
                    value={formats.value(row.compare)}
                />
            )}

            {row.target != null && (
                <TooltipRow swatch={colors.target} label={t("Budget")} value={formats.value(row.target)}/>
            )}
        </React.Fragment>
    );

    const columns: Array<InsightsColumn<CrmTrendRow> | false> = [
        {key: "bucket", label: t("Time range"), kind: "label", cell: row => row.full},
        {key: "value", label: t("Actual"), kind: "value", cell: row => row.value, format: formats.value},
        hasComparison && {
            key: "compare",
            label: t("Comparison"),
            kind: "compare",
            cell: row => row.compare,
            format: formats.value,
            comparison: true,
        },
        hasTarget && {
            key: "target",
            label: t("Budget"),
            kind: "detail",
            cell: row => row.target,
            format: formats.value,
        },
    ];

    return (
        <SeriesCard
            title={title}
            hint={t(definition.hint)}
            headline={headline}
            loading={loading}
            legend={<InsightsSeriesLegend {...spec}/>}
            empty={hasData ? undefined : (
                <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                        {t("No data in the selected period")}
                    </Typography>
                </Stack>
            )}
            table={<InsightsTable label={title} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <InsightsSeriesChart
                    {...spec}
                    height={CHART_HEIGHT}
                    domain={axis.domain}
                    ticks={axis.ticks}
                    formatTick={formats.tick}
                    tooltip={tooltip}
                    marker={markerIndex < 0 ? undefined : {index: markerIndex, label: t("Now")}}
                />
            )}
        />
    );
}

export default CrmTrendCard;
